import { Form, Button, Upload, Table, Alert, Row, Col, Card, Divider, Tooltip } from "antd";
import { useState } from "react";
import { UploadOutlined, DownloadOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import apiPath from "../../constants/apiPath";
import { Download, UploadCloud, CheckCircle2 } from "lucide-react";
import BrandSelect from "../../components/select/BrandSelect";
import CategorySelect from "../../components/select/CategorySelect";
import SegmentSelect from "../../components/select/SegmentSelect";

const BulkUpdateLanguage = () => {

  const api = {
    export    :   apiPath.exportProduct,
    import    :   apiPath.importProduct
  }
    
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [errorLogs, setErrorLogs] = useState([]);
  const [brandId, setBrandId] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [segmentId, setSegmentId] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const downloadProducts = () => {
  if (!brandId) {
    ShowToast("Please select a brand first", Severty.ERROR);
    return;
  }

  setDownloadLoading(true);
  
  let queryString = `?brand_id=${brandId}`;
  if (categoryId) queryString += `&category_id=${categoryId}`;
  if (segmentId) queryString += `&segment_id=${segmentId}`;

  request({
    url: api.export + queryString,
    method: 'GET',
    onSuccess: (data) => {
      setDownloadLoading(false);
      
      const downloadUrl = data.data?.download_url;
      
      if (!downloadUrl) {
        ShowToast("Download URL not available", Severty.ERROR);
        return;
      }
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = data.data?.file_name || `products_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      ShowToast("Download completed successfully", Severty.SUCCESS);
    },
    onError: (error) => {
      setDownloadLoading(false);
      const errorMsg = error.response?.data?.message || error.response?.data?.status || "Download failed";
      ShowToast(errorMsg, Severty.ERROR);
    }
  });
};


  const handleUpload = {
    beforeUpload: (file) => {
      const isExcel = file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || 
                      file.type === "application/vnd.ms-excel" ||
                      file.name.endsWith(".xlsx") ||
                      file.name.endsWith(".xls");
      if (!isExcel) {
        ShowToast("Please upload an Excel file (.xlsx or .xls)", Severty.ERROR);
        return false;
      }
      if (file.size / 1024 / 1024 > 15) {
        ShowToast("File size must be less than 15MB", Severty.ERROR);
        return false;
      }
      setFileList([file]);
      return false;
    },
  };

  const handleImport = () => {
    
    if (!fileList.length) {
      setErrorLogs(["Please select a file first"]);
      return;
    }

    const formData = new FormData();
    formData.append("file", fileList[0]);

    setLoading(true);
    setErrorLogs([]);

    request({
      url: api.import,
      method: "POST",
      data: formData,
      onSuccess: (data) => {
        setLoading(false);
        setImportResults(data.data);
        
        const hasFailed = data.data?.failed > 0;
        
        if (hasFailed && data.data?.errors?.length > 0) {
          const detailedErrors = data.data.errors.map(error => 
            `Row ${error.row} (${error.data?.part_number}): ${error.errors?.join(", ") || "Unknown error"}`
          );

          setErrorLogs(detailedErrors);
          ShowToast("Import completed with errors", Severty.ERROR);

        } else {
          setFileList([]);
          ShowToast(data.message || "Import completed successfully", Severty.SUCCESS);
          
        }
      },
      onError: (error) => {
        setLoading(false);
        const errorMsg = error.response?.data?.message || "Import failed";
        setErrorLogs([errorMsg]);
        setImportResults(error.response?.data);
      },
    });
  };

  const resultsColumns = [
    {
      title: "Part Number",
      dataIndex: "part_number",
      key: "part_number",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span style={{ color: status === "success" ? "green" : "red" }}>
          {status}
        </span>
      ),
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
    },
  ];

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24} xl={24} sm={24} md={24} lg={24}>
          <Form form={form} layout="vertical">
            
            {/* Instructions */}
            <Card className="mb-6 border-l-4 border-l-blue-500">
            
              <p className="text-sm text-gray-600 mb-4">
                Export your products in Excel, update language data in English, Hindi, and Gujarati, then re-import to apply changes:
              </p>

              <Row gutter={[24, 16]}>
                <Col xs={24} md={12}>
                  <div className="space-y-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Step 1: Select Brand</span>
                        <p className="text-gray-600 text-sm mt-1">Choose a brand from the dropdown below</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Step 2: Download Products</span>
                        <p className="text-gray-600 text-sm mt-1">Click "Download Products" to export existing products in Excel format</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Step 3: Edit Language Data</span>
                        <p className="text-gray-600 font-mono bg-gray-100 p-2 rounded mt-1 text-xs space-y-2">
                          Open the downloaded file and update product names & descriptions in multiple languages:<br/>
                          <div className="mb-2 mt-2"> • English (en_name, en_description) </div>
                          <div className="mb-2"> • Hindi (hi_name, hi_description) </div>
                          <div> • Gujarati (gj_name, gj_description) </div>
                        </p>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <div className="space-y-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Step 4: Upload Updated File</span>
                        <p className="text-gray-600 text-sm mt-1">Upload the updated Excel file. System will match by part_number and update all language data automatically</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">File Requirements:</span>
                        <p className="text-gray-600 text-sm mt-1">Maximum <strong>15 MB</strong>, .xlsx or .xls format</p>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              <Divider className="my-4" />

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800 m-0">
                  <strong>Note:</strong> Only <strong>_id</strong> is required to match existing products. 
                  You can update any language field (en_name, hi_name, gj_name, en_description, hi_description, gj_description) 
                  or other fields are not allowed to update by importing functionality. The file must have a header row.
                </p>
              </div>
            </Card>

            {/* Dropdowns Section */}
            <Card className="border-l-4 border-l-blue-500 p-4 rounded-lg mb-6">
              <h3 className="text-md font-semibold text-gray-800 mb-4">Select Filters</h3>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Select Brand"
                    className="font-medium"
                    required
                  >
                    <BrandSelect 
                      value={brandId} 
                      onChange={(val) => {
                        setBrandId(val);
                        setCategoryId(null);
                        setSegmentId(null);
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Select Category (Optional)"
                    className="font-medium"
                  >
                    <CategorySelect
                      brandId={brandId}
                      disabled={!brandId}
                      shouldFetch={!!brandId}
                      value={categoryId}
                      onChange={setCategoryId}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Select Segment (Optional)"
                    className="font-medium"
                  >
                    <SegmentSelect
                      brandId={brandId}
                      disabled={!brandId}
                      shouldFetch={!!brandId}
                      value={segmentId}
                      onChange={setSegmentId}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Download Button */}
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <Tooltip title={!brandId ? "Please select a brand first" : ""}>
                    <Button
                      type="primary"
                      onClick={downloadProducts}
                      loading={downloadLoading}
                      disabled={!brandId}
                      icon={<Download className="w-4 h-4" />}
                      className="flex items-center gap-2"
                    >
                      Download Products
                    </Button>
                  </Tooltip>
                </Col>
              </Row>
            </Card>

            {/* Upload Section */}
            <Card className="border-l-4 border-l-green-500 p-4 rounded-lg mb-6">
              <h3 className="text-md font-semibold text-gray-800 mb-4">Upload Updated File</h3>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Select Excel File"
                    required
                    className="font-medium"
                  >
                    <Upload {...handleUpload} maxCount={1}>
                      <Button className="flex items-center gap-2 w-full justify-center">
                        <UploadCloud className="w-4 h-4" />
                        Click to Upload
                      </Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>

              <div className="flex justify-end gap-3">
                <Button
                  type="primary"
                  loading={loading}
                  onClick={handleImport}
                  disabled={!fileList.length}
                >
                  Import & Update
                </Button>
              </div>
            </Card>

            {/* Import Results */}
            {importResults && (
              <>
                <Alert
                  message={`Import Results: ${importResults.successful || 0} successful, ${importResults.failed || 0} failed`}
                  type={importResults.failed > 0 ? "warning" : "success"}
                  showIcon
                  className="mb-4"
                />

                {importResults.details?.length > 0 && (
                  <div className="mt-4 max-h-[300px] overflow-auto border rounded-md shadow-sm">
                    <Table
                      columns={resultsColumns}
                      dataSource={importResults.details}
                      rowKey={(_, idx) => idx}
                      pagination={false}
                      size="small"
                    />
                  </div>
                )}
              </>
            )}

            {/* Error Logs */}
            {errorLogs.length > 0 && (
              <Alert
                message="Error"
                description={
                  <ul className="m-0 pl-6 list-disc">
                    {errorLogs.map((log, idx) => (
                      <li key={idx}>{log}</li>
                    ))}
                  </ul>
                }
                type="error"
                showIcon
                className="mb-4"
              />
            )}
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default BulkUpdateLanguage;
